import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, Lock } from "lucide-react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmt, when, errText, label, StatusChip, SlaBadge, Section, Field, OPEN, FULFILLABLE } from "./shared";
import { FulfilDialog } from "./FulfilDialog";
import { FailDialog, TextActionDialog } from "./ActionDialogs";

const BASE = "/admin/rewards/fulfilment/orders";
const BTN = "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-50";

function Actions({ data, act, open }) {
  const o = data.order;
  const can = data.permissions.can_fulfil;
  if (!can) {
    return <p className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="gc-detail-view-only"><Lock className="w-3 h-3" /> View only — you don't have the gift card fulfilment permission.</p>;
  }
  if (!OPEN.includes(o.status)) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="gc-detail-actions">
      <button className={`${BTN} border-slate-200 hover:bg-slate-50`} onClick={() => act("assign", {})} data-testid="gc-action-assign-me">Assign to me</button>
      {["AWAITING_FULFILLMENT", "NEEDS_REVIEW"].includes(o.status) && (
        <button className={`${BTN} border-cyan-200 text-cyan-800 hover:bg-cyan-50`} onClick={() => act("start")} data-testid="gc-action-start">Start fulfilment</button>
      )}
      <button className={`${BTN} border-slate-200 hover:bg-slate-50`} onClick={() => open("supplier")} data-testid="gc-action-supplier">Record supplier order</button>
      {FULFILLABLE.includes(o.status) && (
        <button className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold" onClick={() => open("fulfil")} data-testid="gc-action-fulfil">Fulfil Gift Card</button>
      )}
      {o.status === "PENDING_SECOND_APPROVAL" && data.permissions.can_second_approve && (
        <button className={`${BTN} border-indigo-200 text-indigo-800 hover:bg-indigo-50`} onClick={() => act("approve")} data-testid="gc-action-approve">Give second approval</button>
      )}
      {o.status !== "NEEDS_REVIEW" && (
        <button className={`${BTN} border-orange-200 text-orange-800 hover:bg-orange-50`} onClick={() => open("review")} data-testid="gc-action-review">Mark needs review</button>
      )}
      <button className={`${BTN} border-rose-200 text-rose-700 hover:bg-rose-50`} onClick={() => open("fail")} data-testid="gc-action-fail">Unable to Fulfil / Return Cashback</button>
    </div>
  );
}

function Body({ data }) {
  const { order: o, shopper, wallet } = data;
  return (
    <div className="space-y-4">
      <Section title="Shopper" testid="gc-detail-shopper">
        <Field label="Reference" value={shopper.reference} mono testid="gc-detail-shopper-ref" />
        <Field label="Name" value={shopper.name} />
        <Field label="Account status" value={label(shopper.account_status)} testid="gc-detail-account-status" />
      </Section>
      <Section title="Redemption" testid="gc-detail-redemption">
        <Field label="Brand" value={o.brand} testid="gc-detail-brand" />
        <Field label="Face value" value={o.value_minor != null ? fmt(o.value_minor, o.currency) : o.value_display} testid="gc-detail-value" />
        <Field label="Cashback reserved" value={fmt(o.reserved_minor)} testid="gc-detail-reserved" />
        <Field label="Requested" value={when(o.requested_at)} />
        <Field label="Deliver by (24h)" value={when(o.delivery_due_at)} testid="gc-detail-due" />
      </Section>
      <Section title="Fulfilment" testid="gc-detail-fulfilment">
        <Field label="Method" value={label(o.fulfilment_method)} testid="gc-detail-method" />
        <Field label="Assigned to" value={o.assigned_to_name} testid="gc-detail-assignee" />
        <Field label="Supplier" value={o.supplier} />
        <Field label="Supplier order ref" value={o.supplier_order_ref} mono />
        {o.code_masked && <Field label="Gift card code" value={o.code_masked} mono testid="gc-detail-code-masked" />}
        {o.has_pin && <Field label="PIN" value="••••" mono />}
        {o.has_file && <Field label="Card file" value={`${o.file?.content_type} · encrypted`} />}
        {o.needs_review_reason && <Field label="Review reason" value={o.needs_review_reason} />}
        {o.failure_reason && <Field label="Failure reason" value={o.failure_reason} />}
        {o.fulfilled_at && <Field label="Fulfilled" value={when(o.fulfilled_at)} />}
        {o.used_up_at && <Field label="Marked used up by shopper" value={when(o.used_up_at)} testid="gc-detail-used-up" />}
        {o.restored_at && <Field label="Cashback restored" value={when(o.restored_at)} />}
      </Section>
      <Section title="Wallet" testid="gc-detail-wallet">
        <Field label="Reservation status" value={label(wallet.reservation_status)} testid="gc-detail-reservation-status" />
        {wallet.ledger.map((l) => (
          <Field key={l.id} label={label(l.type)} value={`${fmt(l.amount_minor)} · ${when(l.created_at)}`} />
        ))}
      </Section>
    </div>
  );
}

function Timeline({ data, onNote }) {
  return (
    <Section title="Notes & audit trail" testid="gc-detail-timeline">
      <button onClick={onNote} className="text-xs text-cyan-700 font-semibold mb-2" data-testid="gc-action-note">+ Add internal note</button>
      <ul className="space-y-1.5 text-xs">
        {data.order.notes.map((n) => (
          <li key={n.id} className="rounded-lg bg-amber-50/60 border border-amber-100 px-2 py-1.5"><b>{n.by_name}</b> · {when(n.at)}<div className="text-slate-700">{n.text}</div></li>
        ))}
        {data.timeline.map((t, i) => (
          <li key={i} className="flex flex-wrap justify-between gap-x-2 text-slate-600 border-b border-slate-50 py-1">
            <span>{label(t.action.replace("rewards.gift_card_", ""))}{t.meta?.new_state ? ` → ${label(t.meta.new_state)}` : ""}</span>
            <span className="text-slate-400">{t.actor_email || "system"} · {when(t.created_at)}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function OrderDetailSheet({ orderId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = useCallback(async () => {
    if (!orderId) return;
    try { setData((await api.get(`${BASE}/${orderId}`)).data); } catch (e) { toast.error(errText(e, "Could not load order")); }
  }, [orderId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };
  const act = async (path, body) => {
    try { await api.post(`${BASE}/${orderId}/${path}`, body); toast.success("Saved."); refresh(); }
    catch (e) { toast.error(errText(e, "Action failed")); throw e; }
  };
  const o = data?.order;

  return (
    <Sheet open={!!orderId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="gc-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">Order {o?.order_ref || "…"} {o && <StatusChip status={o.status} testid="gc-detail-status" />}</SheetTitle>
          <SheetDescription asChild>
            <div className="flex flex-wrap gap-2 items-center">
              {o && <SlaBadge row={o} testid="gc-detail-sla" />}
              {o?.flags?.map((f) => <span key={f} className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-700" data-testid="gc-detail-flag"><Flag className="w-3 h-3" />{f}</span>)}
              {o?.is_sample && <span className="text-[10px] uppercase tracking-widest text-slate-400">Sample catalogue</span>}
            </div>
          </SheetDescription>
        </SheetHeader>
        {data && (
          <div className="mt-4 space-y-4">
            <Actions data={data} act={(p, b) => act(p, b).catch(() => {})} open={setDialog} />
            <Body data={data} />
            <Timeline data={data} onNote={() => setDialog("note")} />
          </div>
        )}
        <FulfilDialog open={dialog === "fulfil"} onOpenChange={(v) => !v && setDialog(null)} order={o}
                      onChanged={refresh} onDone={() => { setDialog(null); refresh(); }} />
        <FailDialog open={dialog === "fail"} onOpenChange={(v) => !v && setDialog(null)} order={o}
                    onDone={() => { setDialog(null); refresh(); }} />
        <TextActionDialog open={dialog === "review"} onOpenChange={(v) => !v && setDialog(null)} testid="gc-review-dialog"
                          title="Mark needs review" submitLabel="Move to review"
                          description="Cashback stays reserved. Use when the supplier outcome is unclear."
                          fields={[{ key: "reason", label: "What is unclear?", min: 3, placeholder: "e.g. Supplier portal timed out after submitting the order" }]}
                          onSubmit={(v) => act("needs-review", { reason: v.reason })} />
        <TextActionDialog open={dialog === "supplier"} onOpenChange={(v) => !v && setDialog(null)} testid="gc-supplier-dialog"
                          title="Record supplier order" submitLabel="Save supplier details"
                          fields={[{ key: "supplier", label: "Supplier", initial: o?.supplier, rows: 1 },
                                   { key: "supplier_order_ref", label: "Supplier order / reference", initial: o?.supplier_order_ref, rows: 1, optional: true }]}
                          onSubmit={(v) => act("supplier", v)} />
        <TextActionDialog open={dialog === "note"} onOpenChange={(v) => !v && setDialog(null)} testid="gc-note-dialog"
                          title="Add internal note" submitLabel="Add note"
                          description="Internal only. Never paste gift card codes or PINs into notes."
                          fields={[{ key: "text", label: "Note", rows: 3 }]} onSubmit={(v) => act("notes", v)} />
      </SheetContent>
    </Sheet>
  );
}
